// src/contexts/AuthContext.jsx
// Authentication context for managing user state across the app

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.invenire.app';

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);

  // Token management
  const getAccessToken = useCallback(() => {
    return localStorage.getItem('access_token');
  }, []);

  const getRefreshToken = useCallback(() => {
    return localStorage.getItem('refresh_token');
  }, []);

  const setTokens = useCallback((accessToken, refreshToken, expiresIn) => {
    localStorage.setItem('access_token', accessToken);
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    }
    if (expiresIn) {
      const expiresAt = Date.now() + (expiresIn * 1000);
      localStorage.setItem('token_expires_at', expiresAt.toString());
    }
  }, []);

  const clearTokens = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires_at');
  }, []);

  // Check if token is expired or about to expire
  const isTokenExpired = useCallback(() => {
    const expiresAt = localStorage.getItem('token_expires_at');
    if (!expiresAt) return true;
    // Consider expired if less than 5 minutes remaining
    return Date.now() > (parseInt(expiresAt) - 300000);
  }, []);

  // Refresh the access token
  const refreshAccessToken = useCallback(async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken })
    });

    if (!response.ok) {
      throw new Error('Failed to refresh token');
    }

    const data = await response.json();
    setTokens(data.access_token, null, data.expires_in);
    return data.access_token;
  }, [getRefreshToken, setTokens]);

  // Make authenticated API request
  const authFetch = useCallback(async (url, options = {}) => {
    let accessToken = getAccessToken();

    // Check if we have tokens at all
    if (!accessToken) {
      clearTokens();
      setUser(null);
      throw new Error('Not authenticated, please login');
    }

    // Refresh if expired (but only if we have a refresh token)
    if (isTokenExpired()) {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        // No refresh token available - need to re-login
        console.warn('[Auth] Token expired and no refresh token available');
        clearTokens();
        setUser(null);
        throw new Error('Session expired, please login again');
      }
      try {
        accessToken = await refreshAccessToken();
      } catch (err) {
        // Refresh failed, need to re-login
        console.error('[Auth] Token refresh failed:', err);
        clearTokens();
        setUser(null);
        throw new Error('Session expired, please login again');
      }
    }

    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${accessToken}`
    };

    const response = await fetch(url, { ...options, headers });

    // Handle 401 - try refresh once (if we have refresh token)
    if (response.status === 401) {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        console.warn('[Auth] Got 401 and no refresh token available');
        clearTokens();
        setUser(null);
        throw new Error('Session expired, please login again');
      }
      try {
        accessToken = await refreshAccessToken();
        headers['Authorization'] = `Bearer ${accessToken}`;
        return fetch(url, { ...options, headers });
      } catch (err) {
        console.error('[Auth] Token refresh failed after 401:', err);
        clearTokens();
        setUser(null);
        throw new Error('Session expired, please login again');
      }
    }

    return response;
  }, [getAccessToken, getRefreshToken, isTokenExpired, refreshAccessToken, clearTokens]);

  // Fetch current user
  const fetchUser = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await authFetch(`${API_BASE}/auth/me`);
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        clearTokens();
        setUser(null);
      }
    } catch (err) {
      console.error('Failed to fetch user:', err);
      clearTokens();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, authFetch, clearTokens]);

  // Handle OAuth callback (called from callback page)
  const handleCallback = useCallback(async (params) => {
    const { access_token, refresh_token, expires_in } = params;
    
    if (access_token) {
      setTokens(access_token, refresh_token, parseInt(expires_in));
      await fetchUser();
      return true;
    }
    
    return false;
  }, [setTokens, fetchUser]);

  // Login redirect
  const loginWithGoogle = useCallback((redirectUri) => {
    const url = redirectUri 
      ? `${API_BASE}/auth/login/google?redirect_uri=${encodeURIComponent(redirectUri)}`
      : `${API_BASE}/auth/login/google`;
    window.location.href = url;
  }, []);

  const loginWithMicrosoft = useCallback((redirectUri) => {
    const url = redirectUri
      ? `${API_BASE}/auth/login/microsoft?redirect_uri=${encodeURIComponent(redirectUri)}`
      : `${API_BASE}/auth/login/microsoft`;
    window.location.href = url;
  }, []);

  // Logout
  const logout = useCallback(async () => {
    const refreshToken = getRefreshToken();
    
    if (refreshToken) {
      try {
        await fetch(`${API_BASE}/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken })
        });
      } catch (err) {
        console.error('Logout API error:', err);
      }
    }
    
    clearTokens();
    setUser(null);
  }, [getRefreshToken, clearTokens]);

  // Direct login (for email/password authentication)
  const login = useCallback(async (accessToken, refreshToken) => {
    setTokens(accessToken, refreshToken, 3600); // 1 hour
    await fetchUser();
  }, [setTokens, fetchUser]);

  // Check for existing session on mount
  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Helper functions
  const hasRole = useCallback((role) => {
    if (!user) return false;
    return user.roles?.includes(role) || user.roles?.includes('super_admin');
  }, [user]);

  const hasTenantAccess = useCallback((tenantId) => {
    if (!user) return false;
    if (user.roles?.includes('super_admin')) return true;
    return user.tenants?.some(t => t.id === tenantId);
  }, [user]);

  const isAdmin = useCallback(() => {
    return hasRole('admin') || hasRole('super_admin');
  }, [hasRole]);

  const isSuperAdmin = useCallback(() => {
    return hasRole('super_admin');
  }, [hasRole]);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    
    // Auth actions
    login,
    loginWithGoogle,
    loginWithMicrosoft,
    logout,
    handleCallback,
    
    // Token management
    getAccessToken,
    authFetch,
    
    // Role checks
    hasRole,
    hasTenantAccess,
    isAdmin,
    isSuperAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
